import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../utils/apiUtils';

const MonitoringForm = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [monitoringStatus, setMonitoringStatus] = useState('Pending');
  const [signatoryPrefs, setSignatoryPrefs] = useState({
    prepared_by_enabled: true,
    monitored_by_enabled: true,
    reviewed_by_enabled: true
  });

  // Project Details Section
  const [projectDetails, setProjectDetails] = useState({
    date: '',
    extensionProgramTitle: '',
    proponents: '',
    totalProjectCost: '',
    duration: ''
  });

  // Work Plan Section - Activities table
  const [workPlanRows, setWorkPlanRows] = useState([
    {
      id: 1,
      activities: '',
      percentWeight: '',
      physicalTargets: '',
      targetDate: '',
      actualAccomplishment: '',
      percentageAccomplished: '',
      problemsChallenges: ''
    }
  ]);

  // Budget Utilization Section - Dynamic rows
  const [budgetRows, setBudgetRows] = useState([
    {
      id: 1,
      type: 'Purchase Request',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    },
    {
      id: 2,
      type: 'Cash Advance',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    },
    {
      id: 3,
      type: 'Request of Payment',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    }
  ]);

  // Budget totals for manual input
  const [budgetTotals, setBudgetTotals] = useState({
    totalAmountRequested: '',
    totalActualAmount: '',
    totalBudgetUtilized: ''
  });

  // Authentication is handled globally in AuthContext
  
  // Check if user can edit status (Admin only)
  const canEditStatus = user && user.role === 'Admin';

  useEffect(() => {
    fetchProjects();
    // If we have an ID, we're in edit mode - load the monitoring tool data
    if (id) {
      loadMonitoringTool();
    }
  }, [id]);

  // Separate useEffect to handle loading monitoring tool after projects are loaded
  useEffect(() => {
    if (id && projects.length > 0 && !selectedProject) {
      console.log('Projects loaded, now loading monitoring tool...');
      loadMonitoringTool();
    }
  }, [projects, id, selectedProject]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      console.log('MonitoringForm: Fetching projects from /projects/monitoring');
      const { data } = await apiClient.get('/projects/monitoring');
      console.log('MonitoringForm: Received projects:', data);
      setProjects(data);
    } catch (err) {
      console.error('MonitoringForm: Failed to load projects', err);
      console.error('MonitoringForm: Error details:', err.response?.data || err.message);
      // Don't show error message, just set empty array
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMonitoringTool = async () => {
    try {
      setLoading(true);
      console.log('Loading monitoring tool with ID:', id);
      const { data } = await apiClient.get(`/monitoring/${id}`);
      console.log('Loaded monitoring tool:', data);
      
      // Wait for projects to be loaded if they're not already
      if (projects.length === 0) {
        await fetchProjects();
      }
      
      // Set the selected project - try to find it in the current projects array
      console.log('Looking for project_id:', data.project_id, 'in projects:', projects.map(p => p.project_id));
      let project = projects.find(p => p.project_id === data.project_id);
      console.log('Found project in projects array:', project);
      
      // If not found in current projects array, fetch the specific project
      if (!project) {
        try {
          console.log('Project not found in array, fetching specific project...');
          const { data: projectData } = await apiClient.get(`/projects/${data.project_id}`);
          project = projectData;
          console.log('Fetched specific project:', project);
        } catch (projectErr) {
          console.error('Failed to fetch specific project:', projectErr);
        }
      }
      
      if (project) {
        setSelectedProject(project);
        console.log('Selected project set:', project);
      } else {
        console.error('Project not found for project_id:', data.project_id);
        setError('Associated project not found. Please contact support.');
      }
      
      // Populate project details
      // Convert date from ISO string to YYYY-MM-DD format for HTML date input,
      // ensuring it reflects the local date if the UTC time crosses midnight.
      const dateObj = data.date ? new Date(data.date) : null;
      let formattedDate = '';
      if (dateObj) {
        const year = dateObj.getFullYear();
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = dateObj.getDate().toString().padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }
      console.log('Original date from API:', data.date);
      console.log('Formatted date for input (local):', formattedDate);
      
      setProjectDetails({
        date: formattedDate,
        extensionProgramTitle: data.extension_program_title,
        proponents: data.proponents,
        totalProjectCost: data.total_project_cost.toString(),
        duration: data.duration
      });
      
      // Set the monitoring status
      setMonitoringStatus(data.status || 'Pending');

      // Load signatory prefs (default to enabled)
      setSignatoryPrefs({
        prepared_by_enabled: Boolean(Number(data.prepared_by_enabled ?? 1)),
        monitored_by_enabled: Boolean(Number(data.monitored_by_enabled ?? 1)),
        reviewed_by_enabled: Boolean(Number(data.reviewed_by_enabled ?? 1))
      });
      
      // Populate work plan data
      if (data.work_plan && Array.isArray(data.work_plan)) {
        setWorkPlanRows(data.work_plan);
      }
      
      // Populate budget utilization data - always ensure we have the 3 default rows
      const defaultBudgetRows = [
        {
          id: 1,
          type: 'Purchase Request',
          details: '',
          amountRequested: '',
          actualAmount: '',
          budgetUtilized: '',
          remarks: ''
        },
        {
          id: 2,
          type: 'Cash Advance',
          details: '',
          amountRequested: '',
          actualAmount: '',
          budgetUtilized: '',
          remarks: ''
        },
        {
          id: 3,
          type: 'Request of Payment',
          details: '',
          amountRequested: '',
          actualAmount: '',
          budgetUtilized: '',
          remarks: ''
        }
      ];

      if (data.budget_utilization && Array.isArray(data.budget_utilization)) {
        // Merge existing data with default structure
        const mergedRows = defaultBudgetRows.map(defaultRow => {
          const existingRow = data.budget_utilization.find(row => row.type === defaultRow.type);
          return existingRow ? { ...defaultRow, ...existingRow } : defaultRow;
        });
        setBudgetRows(mergedRows);
      } else {
        setBudgetRows(defaultBudgetRows);
      }

      // Load budget totals if they exist
      if (data.budget_totals) {
        setBudgetTotals({
          totalAmountRequested: data.budget_totals.totalAmountRequested || '',
          totalActualAmount: data.budget_totals.totalActualAmount || '',
          totalBudgetUtilized: data.budget_totals.totalBudgetUtilized || ''
        });
      }
      
    } catch (err) {
      console.error('Failed to load monitoring tool:', err);
      setError('Failed to load monitoring tool data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    // Reset form data when project changes
    setSelectedProject(null);
    setError('');
    setSuccess('');
    
    // Reset project details to empty state
    setProjectDetails({
      date: '',
      extensionProgramTitle: '',
      proponents: '',
      totalProjectCost: '',
      duration: ''
    });
    
    // If no project selected, return early
    if (!projectId) {
      return;
    }
    
    // Find and set the selected project
    const project = projects.find(p => p.project_id == projectId);
    setSelectedProject(project);
    
    // Fetch full project details for display
    try {
      const { data: fullProjectData } = await apiClient.get(`/projects/${projectId}`);
      console.log('Full project data loaded:', fullProjectData);
      setSelectedProject(fullProjectData);
      
      // Auto-populate project details from selected project
      setProjectDetails(prev => ({
        ...prev,
        extensionProgramTitle: fullProjectData.title,
        proponents: fullProjectData.project_leader_name
      }));
    } catch (err) {
      console.error('Failed to load full project details', err);
      // Keep the basic project data if full details fail to load
      if (project) {
        setProjectDetails(prev => ({
          ...prev,
          extensionProgramTitle: project.title,
          proponents: project.project_leader_name
        }));
      }
    }
  };

  const handleProjectDetailsChange = (field, value) => {
    if (field === 'totalProjectCost') {
      // Allow commas and decimals for total project cost
      const formattedValue = value.replace(/[^0-9.,]/g, '');
      setProjectDetails(prev => ({
        ...prev,
        [field]: formattedValue
      }));
    } else {
      setProjectDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleWorkPlanChange = (rowId, field, value) => {
    setWorkPlanRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const handleBudgetChange = (rowId, field, value) => {
    setBudgetRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
  };

  const addBudgetRow = () => {
    const newId = Math.max(...budgetRows.map(row => row.id)) + 1;
    setBudgetRows(prev => [...prev, {
      id: newId,
      type: '',
      details: '',
      amountRequested: '',
      actualAmount: '',
      budgetUtilized: '',
      remarks: ''
    }]);
  };

  const removeBudgetRow = () => {
    if (budgetRows.length > 3) {
      setBudgetRows(prev => prev.slice(0, -1));
    }
  };

  const handleBudgetTotalsChange = (field, value) => {
    setBudgetTotals(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWorkPlanRow = () => {
    const newId = Math.max(...workPlanRows.map(row => row.id)) + 1;
    setWorkPlanRows(prev => [...prev, {
      id: newId,
      activities: '',
      percentWeight: '',
      physicalTargets: '',
      targetDate: '',
      actualAccomplishment: '',
      percentageAccomplished: '',
      problemsChallenges: ''
    }]);
  };

  const removeWorkPlanRow = () => {
    if (workPlanRows.length > 1) {
      setWorkPlanRows(prev => prev.slice(0, -1));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Project selection is now optional
      // if (!selectedProject) {
      //   setError('Please select a project');
      //   return;
      // }

      const formData = {
        project_id: selectedProject?.project_id || null,
        date: projectDetails.date,
        extensionProgramTitle: projectDetails.extensionProgramTitle,
        proponents: projectDetails.proponents,
        totalProjectCost: projectDetails.totalProjectCost ? parseFloat(projectDetails.totalProjectCost.replace(/,/g, '')) : 0,
        duration: projectDetails.duration,
        workPlan: workPlanRows,
        budgetUtilization: budgetRows,
        budgetTotals: budgetTotals,
        status: monitoringStatus,
        signatory_prefs: signatoryPrefs
      };

      let response;
      if (id) {
        // Update existing monitoring tool
        response = await apiClient.put(`/monitoring/${id}`, formData);
        console.log('Monitoring tool updated:', response.data);
        setSuccess('Monitoring tool updated successfully!');
      } else {
        // Create new monitoring tool
        response = await apiClient.post('/monitoring', formData);
        console.log('Monitoring tool created:', response.data);
        setSuccess('Monitoring tool created successfully!');
      }
      
      setTimeout(() => {
        navigate('/monitoring');
      }, 2000);

    } catch (err) {
      console.error(`Error ${id ? 'updating' : 'creating'} monitoring tool:`, err);
      if (err.response?.data?.errors) {
        setError(`Validation error: ${err.response.data.errors.map(e => e.msg).join(', ')}`);
      } else if (err.response?.data?.error) {
        setError(`Server error: ${err.response.data.error}`);
      } else {
        setError(`Failed to ${id ? 'update' : 'create'} monitoring tool. Please try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-light min-vh-100">
      <Container className="py-4">
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h3 className="mb-1">{id ? 'Edit Monitoring Tool' : 'Create Monitoring Tool'}</h3>
                <p className="text-muted mb-0">{id ? 'Update the monitoring tool information' : 'Fill out the monitoring tool form'}</p>
              </div>
              <Button variant="outline-secondary" as={Link} to="/monitoring">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Monitoring
              </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form onSubmit={handleSubmit}>
              {/* Project Selection Section - Only show when creating new monitoring tool */}
              {!id && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Select Project</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <div className="mb-2">
                            <Form.Label className="mb-0">Select Project (Approved by Chancellor) for Monitoring Tool (Optional)</Form.Label>
                          </div>
                          <Form.Select
                            value={selectedProject?.project_id || ''}
                            onChange={(e) => handleProjectChange(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">
                              {loading ? 'Loading projects...' : 'Choose an approved project...'}
                            </option>
                            {projects.map(project => (
                              <option key={project.project_id} value={project.project_id}>
                                {project.title}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Only projects approved by the Chancellor are available for monitoring tools
                            {projects.length > 0 && (
                              <span className="ms-2">
                                ({projects.length} approved projects available)
                              </span>
                            )}
                          </Form.Text>
                          {!loading && projects.length === 0 && (
                            <Alert variant="warning" className="mt-2">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              No approved projects available. Projects must be marked as "Approved by Chancellor" before monitoring tools can be created.
                            </Alert>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Project Details Section - Show when project selected or in edit mode */}
              {(selectedProject || id) && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Project Details</h5>
                  </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={projectDetails.date}
                          onChange={(e) => handleProjectDetailsChange('date', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Total Project Cost</Form.Label>
                        <Form.Control
                          type="text"
                          value={projectDetails.totalProjectCost}
                          onChange={(e) => handleProjectDetailsChange('totalProjectCost', e.target.value)}
                          placeholder="Enter total project cost (e.g., 1,000,000.00)"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={projectDetails.extensionProgramTitle}
                          readOnly
                          className="bg-light"
                        />
                        <Form.Text className="text-muted">
                          This field is automatically populated from the selected project
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name of Proponents</Form.Label>
                        <Form.Control
                          type="text"
                          value={projectDetails.proponents}
                          readOnly
                          className="bg-light"
                        />
                        <Form.Text className="text-muted">
                          This field is automatically populated from the selected project
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration (Approved Start & End of Implementation)</Form.Label>
                        <Form.Control
                          type="text"
                          value={projectDetails.duration}
                          onChange={(e) => handleProjectDetailsChange('duration', e.target.value)}
                          placeholder="e.g., January 2024 - December 2024"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
                </Card>
              )}

              {/* Work Plan Section - Show when project selected or in edit mode */}
              {(selectedProject || id) && (
              <Card className="mb-4">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">A. Work Plan</h5>
                    <div className="d-flex gap-2">
                      <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addWorkPlanRow} title="Add row" aria-label="Add Work Plan row">
                        <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                      <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeWorkPlanRow} title="Remove last row" aria-label="Remove Work Plan row" disabled={workPlanRows.length <= 1}>
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th style={{ width: '25%', fontWeight: 'normal' }}>Activities</th>
                          <th style={{ width: '10%', fontWeight: 'normal' }}>Percent Weight of the Activity</th>
                          <th style={{ width: '20%', fontWeight: 'normal' }}>Physical Targets (Quantifiable)</th>
                          <th style={{ width: '15%', fontWeight: 'normal' }}>Target Date of Completion</th>
                          <th style={{ width: '20%', fontWeight: 'normal' }}>Actual Accomplishment</th>
                          <th style={{ width: '10%', fontWeight: 'normal' }}>*Percentage of the Accomplished Tasks</th>
                          <th style={{ width: '15%', fontWeight: 'normal' }}>Problems and Challenges Encountered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workPlanRows.map((row, index) => (
                          <tr key={row.id}>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                value={row.activities}
                                onChange={(e) => handleWorkPlanChange(row.id, 'activities', e.target.value)}
                                placeholder={index === 0 ? "(mention here all activities in your work and financial plan like meeting with partners during planning and preparing of MOU, etc. - all activities conducted related to the project - during and after related to the conduct/implementation of the project/activity; TNA will not be part of this)" : ""}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.percentWeight}
                                onChange={(e) => handleWorkPlanChange(row.id, 'percentWeight', e.target.value)}
                                placeholder="%"
                              />
                            </td>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={row.physicalTargets}
                                onChange={(e) => handleWorkPlanChange(row.id, 'physicalTargets', e.target.value)}
                                placeholder="Target"
                              />
                            </td>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={row.targetDate}
                                onChange={(e) => handleWorkPlanChange(row.id, 'targetDate', e.target.value)}
                                placeholder="Target date"
                              />
                            </td>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={row.actualAccomplishment}
                                onChange={(e) => handleWorkPlanChange(row.id, 'actualAccomplishment', e.target.value)}
                                placeholder="Actual"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.percentageAccomplished}
                                onChange={(e) => handleWorkPlanChange(row.id, 'percentageAccomplished', e.target.value)}
                                placeholder="%"
                                min="0"
                                max="100"
                              />
                            </td>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={row.problemsChallenges}
                                onChange={(e) => handleWorkPlanChange(row.id, 'problemsChallenges', e.target.value)}
                                placeholder="Challenges"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
              )}

              {/* Budget Utilization Section - Show when project selected or in edit mode */}
              {(selectedProject || id) && (
              <Card className="mb-4">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">B. Budget Utilization</h5>
                    <div className="d-flex gap-2">
                      <Button variant="link" className="p-0" style={{ color: '#0d6efd' }} onClick={addBudgetRow} title="Add row" aria-label="Add Budget row">
                        <i className="bi bi-plus-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                      <Button variant="link" className="p-0" style={{ color: '#d32f2f' }} onClick={removeBudgetRow} title="Remove last row" aria-label="Remove Budget row" disabled={budgetRows.length <= 3}>
                        <i className="bi bi-dash-circle-fill" style={{ fontSize: 18 }}></i>
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th style={{ width: '15%', fontWeight: 'normal' }}>Type of Request</th>
                          <th style={{ width: '25%', fontWeight: 'normal' }}>Details of Request</th>
                          <th style={{ width: '15%', fontWeight: 'normal' }}>Amount Requested</th>
                          <th style={{ width: '15%', fontWeight: 'normal' }}>Actual Amount (Amount disbursed)</th>
                          <th style={{ width: '10%', fontWeight: 'normal' }}>Budget Utilized (%)</th>
                          <th style={{ width: '25%', fontWeight: 'normal' }}>Remarks / Challenges Encountered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budgetRows.map((row, index) => (
                          <tr key={row.id}>
                            <td>
                              {row.type}
                            </td>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={row.details}
                                onChange={(e) => handleBudgetChange(row.id, 'details', e.target.value)}
                                placeholder="Details of request"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.amountRequested}
                                onChange={(e) => handleBudgetChange(row.id, 'amountRequested', e.target.value)}
                                placeholder="Amount"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.actualAmount}
                                onChange={(e) => handleBudgetChange(row.id, 'actualAmount', e.target.value)}
                                placeholder="Actual"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.budgetUtilized}
                                onChange={(e) => handleBudgetChange(row.id, 'budgetUtilized', e.target.value)}
                                placeholder="%"
                                min="0"
                                max="100"
                              />
                            </td>
                            <td>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={row.remarks}
                                onChange={(e) => handleBudgetChange(row.id, 'remarks', e.target.value)}
                                placeholder="Remarks"
                              />
                            </td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr>
                          <td><strong>Total</strong></td>
                          <td></td>
                          <td>
                            <Form.Control
                              type="number"
                              value={budgetTotals.totalAmountRequested}
                              onChange={(e) => handleBudgetTotalsChange('totalAmountRequested', e.target.value)}
                              placeholder="Enter total amount requested"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={budgetTotals.totalActualAmount}
                              onChange={(e) => handleBudgetTotalsChange('totalActualAmount', e.target.value)}
                              placeholder="Enter total actual amount"
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={budgetTotals.totalBudgetUtilized}
                              onChange={(e) => handleBudgetTotalsChange('totalBudgetUtilized', e.target.value)}
                              placeholder="Enter total budget utilized"
                            />
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
              )}

              {/* Signatories Section */}
              {(selectedProject || id) && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Signatories</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                      Enable or disable signatories. Disabled signatories will be omitted from the generated DOCX.
                    </div>
                    {[
                      { key: 'prepared_by_enabled', label: 'Prepared By' },
                      { key: 'monitored_by_enabled', label: 'Monitored By (Extension Head)' },
                      { key: 'reviewed_by_enabled', label: 'Reviewed & Evaluated By (VC-RDES)' }
                    ].map(opt => (
                      <div
                        key={opt.key}
                        className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2"
                        role="checkbox"
                        tabIndex={0}
                        aria-checked={!!signatoryPrefs[opt.key]}
                        onClick={() => setSignatoryPrefs(prev => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSignatoryPrefs(prev => ({ ...prev, [opt.key]: !prev[opt.key] }));
                          }
                        }}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <div className="fw-semibold">{opt.label}</div>
                        <Form.Check
                          type="checkbox"
                          checked={!!signatoryPrefs[opt.key]}
                          readOnly
                          style={{ marginBottom: 0, pointerEvents: 'none' }}
                        />
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}

              {/* Status Field - Admin Only - Bottom Section (Edit Mode Only) */}
              {canEditStatus && id && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Monitoring Tool Status</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Status:</strong></Form.Label>
                      <Form.Select
                        value={monitoringStatus}
                        onChange={(e) => setMonitoringStatus(e.target.value)}
                        style={{ border: '1px solid #888', fontSize: '1em' }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Approved by Extension Head">Approved by Extension Head</option>
                        <option value="Rejected by Extension Head">Rejected by Extension Head</option>
                        <option value="Approved by VC-RDES">Approved by VC-RDES</option>
                        <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Only Admin can change monitoring tool status</Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>
              )}

              {/* Submit Buttons - Show when project selected or in edit mode */}
              {(selectedProject || id) && (
              <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" as={Link} to="/monitoring">
                  Cancel
                </Button>
                <Button variant="success" type="submit" disabled={submitting}>
                  {submitting ? (id ? 'Updating...' : 'Creating...') : (id ? 'Update Monitoring Tool' : 'Create Monitoring Tool')}
                </Button>
              </div>
              )}
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MonitoringForm;
